import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import PageLayout from '@components/layout/PageLayout';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardCard from '@components/common/DashboardCard';
import ErrorState from '@components/common/ErrorState';
import Button from '@components/common/Button';
import { AdminQuickActions, LaunchPageWidget } from '@components/features/dashboard';
import type { LaunchPageWidgetDefinition } from '@components/features/dashboard';
import { dashboardService } from '@services';
import { CONTACT_USER_ACTIVATION_WIDGET_KEY, CONTACT_USER_DIVISION_CHANGES_WIDGET_KEY } from '@utils/widgetData';
import type { AdminQuickActionsData } from '@app-types';

/** The Dashboard Widgets of the Admin Launch Page, under their catalog titles. */
const ADMIN_LAUNCH_WIDGETS: LaunchPageWidgetDefinition[] = [
  {
    widgetKey: CONTACT_USER_ACTIVATION_WIDGET_KEY,
    title: 'Activated/Inactivated Contact-Users',
    loadData: () => dashboardService.getContactUserActivationWidgetData()
  },
  {
    widgetKey: CONTACT_USER_DIVISION_CHANGES_WIDGET_KEY,
    title: 'Contact-User Division Changes',
    loadData: () => dashboardService.getContactUserDivisionChangesWidgetData()
  }
];

const AdminLaunchPage: React.FC = () => {
  const { user } = useAuth();

  const {
    data: quickActionsData,
    isLoading: quickActionsLoading,
    isError: quickActionsError,
    refetch: refetchQuickActions
  } = useQuery<AdminQuickActionsData>({
    queryKey: ['admin-quick-actions'],
    queryFn: () => dashboardService.getAdminQuickActions(),
    refetchOnMount: 'always',
    refetchInterval: 60000,
    enabled: !!user
  });

  return (
    <PageLayout>
      <DashboardContainer data-test-id="admin-launch-page">
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Actions waiting for you"
          role="Logged in as Admin"
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />
        <DashboardGrid>
          <DashboardRow>
            <DashboardCard title="Quick Actions">
              {quickActionsError ? (
                <ErrorState
                  data-test-id="admin-quick-actions-error"
                  icon={<AlertCircle size={24} />}
                  message="Failed to load the Quick Action counts."
                  action={
                    <Button variant="secondary" size="sm" onClick={() => refetchQuickActions()}>
                      Try again
                    </Button>
                  }
                />
              ) : (
                <AdminQuickActions data={quickActionsData} isLoading={quickActionsLoading} />
              )}
            </DashboardCard>
          </DashboardRow>
          <DashboardRow variant="2col">
            {ADMIN_LAUNCH_WIDGETS.map((widget) => (
              <DashboardCard key={widget.widgetKey}>
                <LaunchPageWidget widget={widget} />
              </DashboardCard>
            ))}
          </DashboardRow>
        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default AdminLaunchPage;
